import { createParser, useQueryStates } from "nuqs";
import { useCallback, useMemo } from "react";

export interface DateRange {
	from: Date | undefined;
	to: Date | undefined;
}

function formatDate(d: Date): string {
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

function formatMonth(d: Date): string {
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	return `${yyyy}-${mm}`;
}

function isToday(d: Date): boolean {
	const now = new Date();
	return (
		d.getFullYear() === now.getFullYear() &&
		d.getMonth() === now.getMonth() &&
		d.getDate() === now.getDate()
	);
}

function isSameDay(a: Date, b: Date): boolean {
	return formatDate(a) === formatDate(b);
}

function isSameMonth(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

// Parses YYYY-MM-DD as a *local* date (not UTC), matching the calendar UI's
// expectations — nuqs's built-in parseAsIsoDate would shift the day in
// negative-offset timezones. Invalid strings parse to null, so the param is
// simply ignored rather than throwing.
const parseAsLocalDate = createParser<Date>({
	parse(value) {
		const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (!match) return null;
		const d = new Date(+match[1], +match[2] - 1, +match[3]);
		return Number.isNaN(d.getTime()) ? null : d;
	},
	serialize: formatDate,
	eq: (a, b) => formatDate(a) === formatDate(b),
});

// Parses YYYY-MM as a *local* month anchor (first of the month). Independent of
// the date/range selection: it's the calendar's "viewed month" — a browsing
// lens that persists across navigation. Invalid strings parse to null.
const parseAsLocalMonth = createParser<Date>({
	parse(value) {
		const match = value.match(/^(\d{4})-(\d{2})$/);
		if (!match) return null;
		const d = new Date(+match[1], +match[2] - 1, 1);
		return Number.isNaN(d.getTime()) ? null : d;
	},
	serialize: formatMonth,
	eq: (a, b) => formatMonth(a) === formatMonth(b),
});

const dateParams = {
	// Legacy: read-only. Older surfaces (the v2 /calendar) still navigate with
	// `?date=X`; we honor it as the single-day anchor on read, but never write it
	// — selections always serialize to `from`/`to` now, so URLs migrate over time.
	date: parseAsLocalDate,
	from: parseAsLocalDate,
	to: parseAsLocalDate,
	month: parseAsLocalMonth,
};

/**
 * The single source of date selection for the shared (v2) attorney surfaces.
 *
 * The selection lives entirely in `from`/`to`: a single day is `from === to`,
 * and a real range is `from !== to`. There is no `date` param anymore — `date`
 * below is a *derived* convenience anchor (the day Add-entry / SummaryCards
 * default to), not URL state. A legacy `?date=` is still read (back-compat with
 * links from the old /calendar) but never written.
 *
 * The exposed shape is unchanged from before: `dateRange` is populated only for
 * a real multi-day range, so existing `dateRange.from && dateRange.to` "is this
 * a range?" checks keep working — a single day reports an empty `dateRange` and
 * surfaces through `date`.
 */
export function useDateParams() {
	// One nuqs group so a setter can clear several params in one atomic URL
	// write. `history: "replace"` keeps day-to-day navigation out of the back
	// stack. Params omitted from a patch are preserved (e.g. filters).
	const [{ date: legacyDate, from, to, month }, setParams] = useQueryStates(
		dateParams,
		{
			history: "replace",
		},
	);

	// A real range needs two distinct days; `from === to` is a single day, not a
	// range. A lone `from` or `to` (hand-edited URL) is treated as a single day.
	const isRealRange = useMemo(
		() => !!(from && to && !isSameDay(from, to)),
		[from, to],
	);

	// Derived single-day anchor — NOT a URL param. Prefer the selection start,
	// fall back to a legacy `?date=`, then today.
	const date = useMemo(
		() => from ?? to ?? legacyDate ?? new Date(),
		[from, to, legacyDate],
	);

	// Empty unless a real multi-day range is active, preserving the historical
	// contract every consumer relies on to distinguish "range" from "single day".
	const dateRange = useMemo(
		(): DateRange =>
			isRealRange
				? { from: from ?? undefined, to: to ?? undefined }
				: { from: undefined, to: undefined },
		[isRealRange, from, to],
	);

	// Selects a single day: writes `from === to === d`. Today is the default, so
	// it's encoded as the absence of params (clean URL). Always clears the legacy
	// `date` param so the URL migrates to the from/to form.
	const setDate = useCallback(
		(d: Date) => {
			const single = isToday(d) ? null : d;
			setParams({ from: single, to: single, date: null, month: null });
		},
		[setParams],
	);

	// Selects a range. Same from/to params; `date` (legacy) is cleared. Clearing
	// both ends resets to today (the default). `month` is cleared so the calendar
	// re-anchors to the new selection.
	const setDateRange = useCallback(
		(range: DateRange) => {
			setParams({
				from: range.from ?? null,
				to: range.to ?? null,
				date: null,
				month: null,
			});
		},
		[setParams],
	);

	// The calendar's "viewed month" — a browsing lens independent of the
	// selection. Persists in its own `month` param so paging months survives
	// navigation; other surfaces ignore it. Absent `month` falls back to the
	// selection's month (range start / single day), so opening the calendar fresh
	// lands on whatever is selected. Dropped when it equals that fallback.
	const viewedMonth = useMemo(() => {
		const anchor = month ?? from ?? legacyDate ?? new Date();
		return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
	}, [month, from, legacyDate]);

	const setViewedMonth = useCallback(
		(m: Date) => {
			const fallbackAnchor = from ?? legacyDate ?? new Date();
			setParams({ month: isSameMonth(m, fallbackAnchor) ? null : m });
		},
		[setParams, from, legacyDate],
	);

	return {
		date,
		dateRange,
		viewedMonth,
		setDate,
		setDateRange,
		setViewedMonth,
	};
}
